import { FC, useCallback, useMemo, useRef } from 'react';

import {
  IRadioCheckListOption,
  ModifierListItem,
} from 'features/menu/components/product-detail/modifier/modifier-list-item';
import {
  IModifierMultiplier,
  IModifierTypeUIProps,
} from 'features/menu/components/product-detail/modifier/types';
import { logProductModifierCustomization } from 'features/menu/components/product-detail/modifier/utils';
import { MultipliersSelections } from 'features/menu/components/product-wizard/types';

import { ListCardGroup } from 'components/list-card-group';
import { t } from 'state/intl/translations';

export const ModifierMultipleChoices: FC<IModifierTypeUIProps> = ({
  modifier,
  selections,
  onSelectionsChange,
}) => {
  const radioListOptions: IRadioCheckListOption[] = useMemo(
    () => createQuantityOptions(modifier.quantity.max),
    [modifier.quantity.max]
  );

  const queueRef = useRef(new SelectionQueue(modifier.quantity.max, selections));

  const onOptionPress = useCallback(
    (multiplier: IModifierMultiplier, value: string) => {
      const quantity = Number(value);
      const newSelections = queueRef.current.update(multiplier.id, quantity);
      onSelectionsChange(newSelections);
      logProductModifierCustomization(multiplier.name, modifier.name, multiplier.id, quantity);
    },
    [onSelectionsChange, modifier]
  );

  return (
    <ListCardGroup
      isFullContainer
      header={t('chooseItemAmount', { amount: modifier.quantity.max, itemName: modifier.name })}
      items={modifier.multipliers}
      renderItem={({ item: multiplier }: { item: IModifierMultiplier }) => {
        const quantity = selections[multiplier.id] ?? 0;
        return (
          <ModifierListItem
            key={`${multiplier.id}-${quantity}`}
            isSelected={quantity !== 0}
            badgeContent={quantity === 0 ? '' : quantity.toString()}
            content={multiplier}
            options={radioListOptions}
            selectedOption={quantity.toString()}
            itemValue={multiplier}
            onOptionPress={onOptionPress}
          />
        );
      }}
    />
  );
};

function createQuantityOptions(max: number) {
  const opts: IRadioCheckListOption[] = [];
  for (let i = 1; i <= max; i++) {
    var v = i.toString();
    opts.push({ label: v, value: v });
  }
  return opts;
}

/**
 * A queue keeping track of the order of the selections and
 * removes the oldest selections when the max quantity is reached.
 *
 * Example:
 * Say the max quantity is 2. If the user selects a quantity of 2 for BBQ Dipping sauce
 * the queue will look like this: ['BBQ Dipping Sauce', 'BBQ Dipping Sauce']
 * If the user then selects a quantity of 1 for Buffalo Dipping Sauce, the queue will look like this:
 * ['BBQ Dipping sauce', 'BBQ Dipping sauce', 'Buffalo Dipping Sauce']
 * and it will remove the oldest selection, so the queue will look like this: ['BBQ Dipping sauce', 'Buffalo Dipping Sauce']
 */
class SelectionQueue {
  private queue: string[] = [];

  constructor(private readonly maxQuantity: number, selections: MultipliersSelections) {
    // Fill the queue with the selections
    for (const [multiplierId, quantity] of Object.entries(selections)) {
      for (let i = 0; i < quantity; i++) {
        this.queue.push(multiplierId);
      }
    }
  }

  update(id: string, quantity: number): MultipliersSelections {
    // Remove the selections for the id from the queue
    this.queue = this.queue.filter(multiplierId => multiplierId !== id);

    // Add the new selections to the queue
    for (let i = 0; i < quantity; i++) {
      this.queue.push(id);
    }

    // Remove the oldest selections from the queue
    while (this.queue.length > this.maxQuantity) {
      this.queue.shift();
    }

    return this.queue.reduce<MultipliersSelections>((acc, multiplierId) => {
      acc[multiplierId] = (acc[multiplierId] ?? 0) + 1;
      return acc;
    }, {});
  }
}
